import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listIntegrations,
  type Integration,
  type IntegrationConfigKey,
} from "../../lib/api.js";

/**
 * Přehled napojení (API, FTP, importéry, sklady, exporty).
 * Pro každé napojení: stav, env klíče, poslední sync, link na detail.
 */
export function IntegrationsPage(): JSX.Element {
  const [data, setData] = useState<Integration[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    listIntegrations()
      .then((res: { integrations: Integration[] }) => {
        if (mounted) setData(res.integrations);
      })
      .catch((e: unknown) => {
        if (mounted) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900">Napojení</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Přehled datových napojení — API, FTP, importéry, sklady a exporty (klíče
          a poslední import do databáze).{" "}
          <Link to="/admin/automations" className="text-ktm-orange hover:underline">
            Kdo co spouští a jestli to dnes doběhlo → Automatizace
          </Link>
          .
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-6">
          Chyba načtení: {error}
        </div>
      )}

      {!data && !error && (
        <div className="text-sm text-zinc-500">Načítám…</div>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((it) => (
            <IntegrationCard key={it.id} item={it} />
          ))}
        </div>
      )}

      <div className="mt-10 text-xs text-zinc-400">
        Stav konfigurace se čte z env proměnných backendu. Hodnoty kódů a hesel zde
        nikdy nezobrazujeme — jen příznak „nastaveno / chybí".
      </div>
    </div>
  );
}

function IntegrationCard({ item }: { item: Integration }): JSX.Element {
  const statusBadge = STATUS_BADGE[item.status]!;
  const kindBadge = KIND_BADGE[item.kind]!;

  return (
    <div className="bg-white rounded-xl ring-1 ring-zinc-200 shadow-sm overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 border-b border-zinc-100 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`inline-block w-2 h-2 rounded-full ${statusBadge.dot}`}
              aria-hidden
            />
            <h2 className="font-semibold text-zinc-900 truncate">{item.label}</h2>
          </div>
          <span
            className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${kindBadge.bg} ${kindBadge.text}`}
          >
            {kindBadge.label}
          </span>
        </div>
        <span
          className={`shrink-0 text-xs font-medium px-2 py-1 rounded-full ${statusBadge.pill}`}
        >
          {statusBadge.label}
        </span>
      </div>

      {/* Body */}
      <div className="px-5 py-4 flex-1 space-y-3">
        <p className="text-sm text-zinc-600 leading-relaxed">{item.description}</p>

        {item.configKeys.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1.5">
              Konfigurace
            </div>
            <div className="space-y-1">
              {item.configKeys.map((c: IntegrationConfigKey) => (
                <div
                  key={c.key}
                  className="flex items-center justify-between text-xs"
                >
                  <code className="font-mono text-zinc-700">{c.key}</code>
                  {c.set ? (
                    <span className="text-emerald-600 font-medium">✓ nastaveno</span>
                  ) : (
                    <span className="text-red-500 font-medium">✗ chybí</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {item.lastRun && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-1.5">
              Poslední běh
            </div>
            <div className="rounded-lg bg-zinc-50 px-3 py-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <RunStatusPill status={item.lastRun.status} />
                {item.lastRun.at && (
                  <span className="text-zinc-500">{formatDate(item.lastRun.at)}</span>
                )}
              </div>
              <div className="mt-1 text-zinc-700">{item.lastRun.summary}</div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {item.detailRoute && (
        <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50/50">
          <Link
            to={item.detailRoute}
            className="text-sm font-medium text-ktm-orange hover:text-orange-700 transition-colors flex items-center gap-1"
          >
            Otevřít detail
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </Link>
        </div>
      )}
    </div>
  );
}

function RunStatusPill({ status }: { status: string }): JSX.Element {
  const map: Record<string, string> = {
    ok: "bg-emerald-100 text-emerald-700",
    success: "bg-emerald-100 text-emerald-700",
    partial: "bg-amber-100 text-amber-700",
    running: "bg-blue-100 text-blue-700",
    failed: "bg-red-100 text-red-700",
    error: "bg-red-100 text-red-700",
  };
  const cls = map[status] ?? "bg-zinc-100 text-zinc-700";
  return (
    <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>
      {status}
    </span>
  );
}

function formatDate(iso: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("cs-CZ", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const STATUS_BADGE: Record<
  Integration["status"],
  { label: string; pill: string; dot: string }
> = {
  active: {
    label: "aktivní",
    pill: "bg-emerald-100 text-emerald-700",
    dot: "bg-emerald-500",
  },
  configured: {
    label: "nakonfigurováno",
    pill: "bg-amber-100 text-amber-700",
    dot: "bg-amber-500",
  },
  unconfigured: {
    label: "chybí konfig",
    pill: "bg-red-100 text-red-700",
    dot: "bg-red-500",
  },
};

const KIND_BADGE: Record<
  Integration["kind"],
  { label: string; bg: string; text: string }
> = {
  api: { label: "API", bg: "bg-violet-100", text: "text-violet-700" },
  ftp: { label: "FTP", bg: "bg-blue-100", text: "text-blue-700" },
  importer: { label: "Importer", bg: "bg-cyan-100", text: "text-cyan-700" },
  warehouse: { label: "Sklad", bg: "bg-orange-100", text: "text-orange-700" },
  export: { label: "Export", bg: "bg-zinc-200", text: "text-zinc-700" },
  translation: { label: "Překlad", bg: "bg-pink-100", text: "text-pink-700" },
};
